import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useSmsWorkspaceTemplates } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import RouteConstants from '@/features/router/RouteConstants';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { Button } from '@/lib';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFileAlt } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { SmsTemplatesViewMode } from '../config';
import { SmsTemplate } from '../smsTemplateListFlowMapper';
import { SmsTemplatePreviewSlideOver } from '../SmsTemplatePreviewSlideOver';
import { SmsTemplateListContent } from './SmsTemplateListContent';
import { SmsTemplateListControls } from './SmsTemplateListControls';
import { SmsTemplateListEmptyState } from './SmsTemplateListEmptyState';
import { SmsTemplateListErrorState } from './SmsTemplateListErrorState';
import { SmsTemplateListHeader } from './SmsTemplateListHeader';
import { SmsTemplateListLoadingState } from './SmsTemplateListLoadingState';

const ConnectorNotConnectedBlock: FC = () => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('smsWorkspace.templates.noConnectorTitle')}</h2>
      <p className='max-w-xl text-center text-secondary-600'>{t('smsWorkspace.templates.noConnectorDescription')}</p>
      <FaRegFileAlt className='size-12 text-gray-400' />
      <Button onClick={() => history.push(RouteConstants.destinations)} text={t('goToDestinationsText')} />
    </div>
  );
};

export const SmsTemplateList: FC = () => {
  const history = useHistory();
  const { t } = useTranslation('campaign');
  const { openSlideOver } = useSlideOverContext();
  const { isAudienceSettled, hasAudience, isLoading: isAudienceLoading } = useFirstAudienceId();
  const { templates, isLoading, isError, reload, missingInstance, missingAudience, isInsertReady, isEditReady, isDeleteReady } =
    useSmsWorkspaceTemplates();

  const [searchValue, setSearchValue] = useState('');
  const [viewMode, setViewMode] = useState<SmsTemplatesViewMode>('cards');
  const canCreateTemplate = !isLoading && isInsertReady;

  const filteredItems = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return templates;
    }
    return templates.filter((template) => template.name.toLowerCase().includes(query) || template.id.toLowerCase().includes(query));
  }, [searchValue, templates]);

  const handleCreateTemplate = () => {
    if (!canCreateTemplate) {
      return;
    }
    history.push(RouteConstants.createSmsTemplate);
  };

  const handlePreviewTemplate = (template: SmsTemplate) => {
    // return for now
    return;
    openSlideOver({
      renderContent: SmsTemplatePreviewSlideOver,
      renderContentProps: { template },
      showButtonInsideSlideOver: true,
    });
  };

  if (isAudienceLoading) {
    return <SmsTemplateListLoadingState />;
  }

  if (missingAudience || (isAudienceSettled && !hasAudience)) {
    return (
      <section className='space-y-5 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
        <SmsTemplateListHeader onCreate={handleCreateTemplate} isCreateDisabled />
        <AudienceRequiredEmptyState />
      </section>
    );
  }

  if (missingInstance) {
    return (
      <section className='space-y-5 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
        <SmsTemplateListHeader onCreate={handleCreateTemplate} isCreateDisabled />
        <ConnectorNotConnectedBlock />
      </section>
    );
  }

  const showError = !isLoading && isError;

  return (
    <section className='space-y-5 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <SmsTemplateListHeader onCreate={handleCreateTemplate} isCreateDisabled={!canCreateTemplate} />

      {isLoading ? <SmsTemplateListLoadingState /> : null}

      {showError ? <SmsTemplateListErrorState onRetry={() => void reload()} /> : null}

      {!isLoading && !showError ? (
        <>
          {templates.length > 0 ? (
            <SmsTemplateListControls
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              filteredCount={filteredItems.length}
              totalCount={templates.length}
              onEditHub={() => history.push(RouteConstants.smsTemplatesEditHub)}
              onDeleteHub={() => history.push(RouteConstants.smsTemplatesDeleteHub)}
              isEditHubDisabled={!isEditReady}
              isDeleteHubDisabled={!isDeleteReady}
            />
          ) : null}

          {filteredItems.length > 0 ? (
            <SmsTemplateListContent viewMode={viewMode} items={filteredItems} onSelectTemplate={handlePreviewTemplate} />
          ) : (
            <SmsTemplateListEmptyState
              variant={templates.length === 0 ? 'global' : 'filtered'}
              onCreate={templates.length === 0 && canCreateTemplate ? handleCreateTemplate : undefined}
            />
          )}
        </>
      ) : null}

      {!isLoading && !showError && templates.length > 0 ? <p className='text-xs text-quaternary-500'>{t('smsWorkspace.templates.hubHint')}</p> : null}
    </section>
  );
};
